class PdpZoom {
  constructor(root) {
    this.root = root;
    this.main = root.querySelector('[data-pdp-zoom-main]');
    this.stage = root.querySelector('[data-pdp-zoom-stage]');
    this.thumbs = Array.from(root.querySelectorAll('[data-pdp-zoom-thumb]'));
    this.index = 0;
    this.animating = false;
    this.clickZoomScale = Number(root.dataset.clickZoomScale) || 2;
    this.scale = 1;
    this.translateX = 0;
    this.translateY = 0;
    this.maxScale = Math.max(4, this.clickZoomScale);
    this.gesture = null;
    this.ignoreNextClick = false;
    this.modalAnimations = [];

    root.querySelector('[data-pdp-zoom-close]')?.addEventListener('click', () => this.close());
    root.querySelector('[data-pdp-zoom-prev]')?.addEventListener('click', () => this.go(-1));
    root.querySelector('[data-pdp-zoom-next]')?.addEventListener('click', () => this.go(1));
    this.thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => this.show(index, index > this.index ? 1 : -1)));
    this.stage?.addEventListener('click', (event) => this.onImageClick(event));
    this.stage?.addEventListener('pointermove', (event) => this.onPointerMove(event), { passive: true });
    this.stage?.addEventListener('touchstart', (event) => this.onTouchStart(event), { passive: true });
    this.stage?.addEventListener('touchmove', (event) => this.onTouchMove(event), { passive: false });
    this.stage?.addEventListener('touchend', (event) => this.onTouchEnd(event));
    this.stage?.addEventListener('touchcancel', (event) => this.onTouchEnd(event));
    this.stage?.addEventListener('gesturestart', (event) => event.preventDefault());
    this.stage?.addEventListener('gesturechange', (event) => event.preventDefault());
  }

  async open(index = 0) {
    this.cancelModalAnimations();
    document.body.classList.add('pdp-zoom-open');
    await this.show(index, 0, false);

    this.root.hidden = false;
    this.root.setAttribute('aria-hidden', 'false');
    this.thumbs[index]?.scrollIntoView({ block: 'nearest', inline: 'center' });

    if (this.root.animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const rootAnimation = this.root.animate(
        [{ opacity: 0 }, { opacity: 1 }],
        { duration: 300, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
      );
      const stageAnimation = this.stage.animate(
        [{ opacity: 0.65, transform: 'scale(0.985)' }, { opacity: 1, transform: 'scale(1)' }],
        { duration: 420, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
      );
      this.modalAnimations = [rootAnimation, stageAnimation];
      await Promise.all(this.modalAnimations.map((animation) => animation.finished.catch(() => {})));
      this.modalAnimations = [];
    }

    this.root.querySelector('[data-pdp-zoom-close]')?.focus();
  }

  async close() {
    if (this.root.hidden) return;
    this.cancelModalAnimations();

    if (this.root.animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const rootAnimation = this.root.animate(
        [{ opacity: 1 }, { opacity: 0 }],
        { duration: 240, easing: 'cubic-bezier(0.4, 0, 1, 1)', fill: 'forwards' }
      );
      const stageAnimation = this.stage.animate(
        [{ opacity: 1, transform: 'scale(1)' }, { opacity: 0.7, transform: 'scale(0.99)' }],
        { duration: 240, easing: 'cubic-bezier(0.4, 0, 1, 1)', fill: 'forwards' }
      );
      this.modalAnimations = [rootAnimation, stageAnimation];
      await Promise.all(this.modalAnimations.map((animation) => animation.finished.catch(() => {})));
    }

    this.root.hidden = true;
    this.root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('pdp-zoom-open');
    this.resetTransform();
    this.cancelModalAnimations();
  }

  cancelModalAnimations() {
    this.modalAnimations.forEach((animation) => animation.cancel());
    this.modalAnimations = [];
  }

  async show(index, direction = 0, animate = true) {
    const visibleIndexes = this.thumbs
      .map((thumb, thumbIndex) => thumb.hidden ? -1 : thumbIndex)
      .filter((thumbIndex) => thumbIndex !== -1);
    if (this.animating || !visibleIndexes.length) return;
    const normalized = visibleIndexes.includes(index) ? index : visibleIndexes[0];
    const thumb = this.thumbs[normalized];
    const src = thumb.dataset.zoomSrc;
    if (!src) return;

    this.index = normalized;
    this.thumbs.forEach((item, itemIndex) => item.classList.toggle('is-active', itemIndex === normalized));
    if (!this.root.hidden) {
      thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }

    this.animating = true;
    await this.preload(src);
    this.resetTransform();

    if (animate && direction && this.main.src && this.main.animate) {
      const stageRect = this.stage.getBoundingClientRect();
      const mainRect = this.main.getBoundingClientRect();
      const clone = this.main.cloneNode(true);
      const enterDistance = direction > 0 ? '7%' : '-7%';
      const exitDistance = direction > 0 ? '-7%' : '7%';
      clone.className = 'pdpZoom__mainImageClone';
      clone.removeAttribute('data-pdp-zoom-main');
      clone.removeAttribute('id');
      clone.style.left = `${mainRect.left - stageRect.left}px`;
      clone.style.top = `${mainRect.top - stageRect.top}px`;
      clone.style.width = `${mainRect.width}px`;
      clone.style.minWidth = `${mainRect.width}px`;
      clone.style.height = `${mainRect.height}px`;
      this.stage.appendChild(clone);

      this.main.src = src;
      this.main.alt = thumb.querySelector('img')?.alt || '';

      const animationOptions = {
        duration: 420,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'forwards'
      };
      const entering = this.main.animate(
        [
          { opacity: 0.35, transform: `translate3d(${enterDistance}, 0, 0) scale(1.01)` },
          { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' }
        ],
        animationOptions
      );
      const leaving = clone.animate(
        [
          { offset: 0, opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
          { offset: 0.72, opacity: 0, transform: `translate3d(${exitDistance}, 0, 0) scale(0.99)` },
          { offset: 1, opacity: 0, transform: `translate3d(${exitDistance}, 0, 0) scale(0.99)` }
        ],
        animationOptions
      );

      await Promise.all([
        entering.finished.catch(() => {}),
        leaving.finished.catch(() => {})
      ]);
      entering.cancel();
      clone.remove();
      this.main.style.transform = '';
    } else {
      this.main.src = src;
      this.main.alt = thumb.querySelector('img')?.alt || '';
    }

    this.animating = false;
  }

  go(direction) {
    const visibleIndexes = this.thumbs
      .map((thumb, thumbIndex) => thumb.hidden ? -1 : thumbIndex)
      .filter((thumbIndex) => thumbIndex !== -1);
    if (!visibleIndexes.length) return;
    const currentPosition = visibleIndexes.indexOf(this.index);
    const nextPosition = (currentPosition + direction + visibleIndexes.length) % visibleIndexes.length;
    this.show(visibleIndexes[nextPosition], direction);
  }

  preload(src) {
    return new Promise((resolve) => {
      const image = new Image();
      image.onload = () => {
        if (image.decode) {
          image.decode().catch(() => {}).finally(resolve);
          return;
        }
        resolve();
      };
      image.onerror = resolve;
      image.src = src;
    });
  }

  resetTransform() {
    this.scale = 1;
    this.translateX = 0;
    this.translateY = 0;
    this.gesture = null;
    if (this.main) this.main.style.transform = '';
    this.root.classList.remove('is-zoomed');
  }

  onImageClick(event) {
    if (event.target !== this.main || this.animating) return;
    if (this.ignoreNextClick) {
      this.ignoreNextClick = false;
      return;
    }

    if (this.scale > 1) {
      this.resetTransform();
      return;
    }

    const rect = this.main.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    this.scale = this.clickZoomScale;
    this.translateX = (event.clientX - rect.left - rect.width / 2) * (1 - this.scale);
    this.translateY = (event.clientY - rect.top - rect.height / 2) * (1 - this.scale);
    this.clampTranslate();
    this.renderTransform();
  }

  onPointerMove(event) {
    if (this.scale <= 1 || this.animating || event.pointerType === 'touch') return;

    const rect = this.main.getBoundingClientRect();
    const imageCenterX = rect.left + rect.width / 2 - this.translateX;
    const imageCenterY = rect.top + rect.height / 2 - this.translateY;

    this.translateX = (event.clientX - imageCenterX) * (1 - this.scale);
    this.translateY = (event.clientY - imageCenterY) * (1 - this.scale);
    this.clampTranslate();
    this.renderTransform();
  }

  renderTransform() {
    if (!this.main) return;
    this.main.style.transform = `translate3d(${this.translateX}px, ${this.translateY}px, 0) scale(${this.scale})`;
    this.root.classList.toggle('is-zoomed', this.scale > 1);
  }

  clampTranslate() {
    if (!this.main || !this.stage) return;
    const maxX = Math.max(0, (this.main.offsetWidth * this.scale - this.stage.clientWidth) / 2);
    const maxY = Math.max(0, (this.main.offsetHeight * this.scale - this.stage.clientHeight) / 2);
    this.translateX = Math.min(maxX, Math.max(-maxX, this.translateX));
    this.translateY = Math.min(maxY, Math.max(-maxY, this.translateY));
  }

  pinchDistance(first, second) {
    return Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY);
  }

  pinchCenter(first, second) {
    return { x: (first.clientX + second.clientX) / 2, y: (first.clientY + second.clientY) / 2 };
  }

  beginPinch(touches) {
    const rect = this.main.getBoundingClientRect();
    this.gesture = {
      mode: 'pinch',
      distance: this.pinchDistance(touches[0], touches[1]) || 1,
      center: this.pinchCenter(touches[0], touches[1]),
      origin: {
        x: rect.left + rect.width / 2 - this.translateX,
        y: rect.top + rect.height / 2 - this.translateY
      },
      scale: this.scale,
      translateX: this.translateX,
      translateY: this.translateY
    };
  }

  beginDrag(touch) {
    this.gesture = {
      mode: 'drag',
      x: touch.clientX,
      y: touch.clientY,
      translateX: this.translateX,
      translateY: this.translateY
    };
  }

  onTouchStart(event) {
    if (!this.main) return;
    if (event.touches.length >= 2) {
      this.beginPinch(event.touches);
      return;
    }
    if (event.touches.length === 1) this.beginDrag(event.touches[0]);
  }

  onTouchMove(event) {
    const gesture = this.gesture;
    if (!gesture) return;

    if (event.touches.length >= 2) {
      event.preventDefault();
      if (gesture.mode !== 'pinch') {
        this.beginPinch(event.touches);
        return;
      }

      const distance = this.pinchDistance(event.touches[0], event.touches[1]);
      const center = this.pinchCenter(event.touches[0], event.touches[1]);
      const scale = Math.min(this.maxScale, Math.max(1, (gesture.scale * distance) / gesture.distance));
      const pointX = (gesture.center.x - gesture.origin.x - gesture.translateX) / gesture.scale;
      const pointY = (gesture.center.y - gesture.origin.y - gesture.translateY) / gesture.scale;

      this.scale = scale;
      this.translateX = center.x - gesture.origin.x - pointX * scale;
      this.translateY = center.y - gesture.origin.y - pointY * scale;
      this.clampTranslate();
      this.renderTransform();
      return;
    }

    if (event.touches.length === 1 && gesture.mode === 'drag' && this.scale > 1) {
      event.preventDefault();
      this.translateX = gesture.translateX + (event.touches[0].clientX - gesture.x);
      this.translateY = gesture.translateY + (event.touches[0].clientY - gesture.y);
      this.clampTranslate();
      this.renderTransform();
    }
  }

  onTouchEnd(event) {
    const gesture = this.gesture;

    if (event.touches.length >= 2) {
      this.beginPinch(event.touches);
      return;
    }
    if (event.touches.length === 1) {
      this.beginDrag(event.touches[0]);
      return;
    }

    this.gesture = null;
    if (this.scale > 1) return;
    this.resetTransform();

    if (event.type !== 'touchend' || !gesture || gesture.mode !== 'drag') return;
    const touch = event.changedTouches[0];
    if (!touch) return;
    const deltaX = touch.clientX - gesture.x;
    const deltaY = touch.clientY - gesture.y;
    if (Math.abs(deltaX) > 48 && Math.abs(deltaX) > Math.abs(deltaY)) {
      this.ignoreNextClick = true;
      window.setTimeout(() => { this.ignoreNextClick = false; }, 400);
      this.go(deltaX > 0 ? -1 : 1);
    }
  }
}

class PdpInlineImageZoom {
  constructor(root) {
    this.root = root;
    this.zoomed = false;
    this.mobileSlider = root.closest('.pdp-mobile-slider');
    this.sliderDragInitiallyEnabled = null;
    this.panX = 0;
    this.panY = 0;
    this.panOriginX = 0.5;
    this.panOriginY = 0.5;
    this.touchPan = null;

    this.onClick = this.onClick.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);

    root.addEventListener('click', this.onClick);
    root.addEventListener('pointermove', this.onPointerMove, { passive: true });
    if (this.mobileSlider) {
      root.addEventListener('touchstart', this.onTouchStart, { passive: true });
      root.addEventListener('touchmove', this.onTouchMove, { passive: false });
      root.addEventListener('touchend', this.onTouchEnd, { passive: true });
      root.addEventListener('touchcancel', this.onTouchEnd, { passive: true });
    }
  }

  onClick(event) {
    if (event.target.closest('[data-pdp-zoom-open]')) return;

    this.zoomed = !this.zoomed;
    this.root.classList.toggle('is-zoomed', this.zoomed);
    this.root.setAttribute('aria-pressed', String(this.zoomed));

    if (this.zoomed) {
      this.setSliderDragging(false);
      this.panX = 0;
      this.panY = 0;
      this.root.style.removeProperty('--pdp-zoom-pan-x');
      this.root.style.removeProperty('--pdp-zoom-pan-y');
      this.updateOrigin(event);
      return;
    }

    this.reset();
  }

  reset() {
    this.zoomed = false;
    this.root.classList.remove('is-zoomed');
    this.root.setAttribute('aria-pressed', 'false');
    this.root.style.removeProperty('--pdp-zoom-x');
    this.root.style.removeProperty('--pdp-zoom-y');
    this.root.style.removeProperty('--pdp-zoom-pan-x');
    this.root.style.removeProperty('--pdp-zoom-pan-y');
    this.root.classList.remove('is-panning');
    this.panX = 0;
    this.panY = 0;
    this.touchPan = null;
    this.setSliderDragging(true);
  }

  setSliderDragging(enabled) {
    const slider = this.root.closest('.splide')?.splide;
    const drag = slider?.Components?.Drag;
    if (!drag?.disable) return;

    if (!enabled) {
      if (this.sliderDragInitiallyEnabled === null) {
        this.sliderDragInitiallyEnabled = slider.options?.drag !== false;
      }
      if (this.sliderDragInitiallyEnabled) drag.disable(true);
      return;
    }

    if (this.sliderDragInitiallyEnabled !== null) {
      drag.disable(!this.sliderDragInitiallyEnabled);
      this.sliderDragInitiallyEnabled = null;
    }
  }

  onPointerMove(event) {
    if (!this.zoomed || this.mobileSlider) return;
    this.updateOrigin(event);
  }

  onTouchStart(event) {
    if (!this.zoomed || !this.mobileSlider || event.touches.length !== 1) return;

    const touch = event.touches[0];
    this.touchPan = {
      startX: touch.clientX,
      startY: touch.clientY,
      panX: this.panX,
      panY: this.panY
    };
    this.root.classList.add('is-panning');
  }

  onTouchMove(event) {
    if (!this.zoomed || !this.mobileSlider) return;
    if (event.cancelable) event.preventDefault();
    if (event.touches.length !== 1 || !this.touchPan) return;

    const touch = event.touches[0];
    this.panX = this.clampPan(this.touchPan.panX + touch.clientX - this.touchPan.startX, 'x');
    this.panY = this.clampPan(this.touchPan.panY + touch.clientY - this.touchPan.startY, 'y');
    this.root.style.setProperty('--pdp-zoom-pan-x', `${this.panX}px`);
    this.root.style.setProperty('--pdp-zoom-pan-y', `${this.panY}px`);
    this.root.classList.add('is-panning');
  }

  onTouchEnd() {
    this.touchPan = null;
    this.root.classList.remove('is-panning');
  }

  clampPan(value, axis) {
    const rect = this.root.getBoundingClientRect();
    const scale = Number.parseFloat(getComputedStyle(this.root).getPropertyValue('--pdp-image-click-zoom')) || 2;
    const origin = axis === 'x' ? this.panOriginX : this.panOriginY;
    const size = axis === 'x' ? rect.width : rect.height;
    const min = -size * (1 - origin) * (scale - 1);
    const max = size * origin * (scale - 1);
    return Math.min(max, Math.max(min, value));
  }

  updateOrigin(event) {
    const rect = this.root.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));
    this.panOriginX = x / 100;
    this.panOriginY = y / 100;
    this.root.style.setProperty('--pdp-zoom-x', `${x}%`);
    this.root.style.setProperty('--pdp-zoom-y', `${y}%`);
  }
}

class PdpZoomManager {
  constructor() {
    this.instances = new Map();
    this.inlineInstances = new Map();
    this.init();
    document.addEventListener('click', (event) => this.onClick(event));
    document.addEventListener('keydown', (event) => this.onKeydown(event));
    document.addEventListener('shopify:section:load', () => this.init());
  }

  init() {
    document.querySelectorAll('[data-pdp-zoom]').forEach((root) => {
      if (!this.instances.has(root)) this.instances.set(root, new PdpZoom(root));
    });
    document.querySelectorAll('[data-pdp-zoom-direct]').forEach((root) => {
      if (!this.inlineInstances.has(root)) this.inlineInstances.set(root, new PdpInlineImageZoom(root));
    });
  }

  current() {
    return Array.from(this.instances.values()).find((instance) => !instance.root.hidden);
  }

  onClick(event) {
    const galleryThumb = event.target.closest('.pdp-desktop-thumbnail[href^="#img-"]');
    if (galleryThumb) {
      const section = galleryThumb.closest('[data-pdp-section-id]');
      const targetId = galleryThumb.getAttribute('href')?.slice(1);
      const target = targetId ? section?.querySelector(`#${CSS.escape(targetId)}`) : null;
      if (!target) return;

      event.preventDefault();
      const gallery = section?.querySelector('[data-pdp-desktop-gallery]');
      if (gallery) {
        const galleryLeft = gallery.getBoundingClientRect().left;
        const imageLeft = target.getBoundingClientRect().left;
        gallery.scrollTo({
          left: Math.max(0, gallery.scrollLeft + imageLeft - galleryLeft),
          behavior: 'smooth',
        });
      } else {
        const thumbnailTopOffset = 96;
        window.scrollTo({
          top: Math.max(0, window.scrollY + target.getBoundingClientRect().top - thumbnailTopOffset),
          behavior: 'smooth',
        });
      }
      return;
    }

    const trigger = event.target.closest('[data-pdp-zoom-open]');
    if (!trigger) return;
    const section = trigger.closest('[data-pdp-section-id]');
    const root = section?.querySelector('[data-pdp-zoom]');
    const instance = this.instances.get(root);
    if (!instance) return;
    event.preventDefault();
    const directTrigger = trigger.closest('.product__imageWrapper')?.querySelector('[data-pdp-zoom-direct]');
    this.inlineInstances.get(directTrigger)?.reset();
    instance.open(Number(trigger.dataset.zoomIndex || 0));
  }

  onKeydown(event) {
    const instance = this.current();
    if (!instance) return;
    if (event.key === 'Escape') instance.close();
    if (event.key === 'ArrowLeft') instance.go(-1);
    if (event.key === 'ArrowRight') instance.go(1);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => { window.PdpZoomManager = new PdpZoomManager(); });
} else {
  window.PdpZoomManager = new PdpZoomManager();
}
